/* eslint-disable max-statements */
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { ExclamationCircleIcon } from "@heroicons/react/outline";
import PropType from "prop-types";
import cancelRegistrationMutation from "./cancelRegistrationMutation";

import CoursesList from "../components/CoursesList/CoursesList";
import { useAuth } from "./wp.auth.service";
import { useNotifications } from "../components/NotificationsContext";

const getRegistrationIdFromCourseId = (courseId, registrations) => {
	if (registrations[courseId]) {
		return registrations[courseId].id;
	}
	return false;
};

const CancelCourseRegistrationModal = ({
	course,
	onCancel,
	show
}) => {
	const queryClient = useQueryClient();
	const { user, refetchUser } = useAuth();
	const { addNotification } = useNotifications();

	const [isLoading, setIsLoading] = useState(false);

	const {
		mutateAsync
	} = cancelRegistrationMutation(useMutation);
	const [courseToCancel, setCourseToCancel] = useState(null);

	useEffect(() => {
		if (show) {
			setCourseToCancel({
				...course
			});
		}
	}, [show]);

	const handleCancelReg = useCallback(async () => {
		setIsLoading(() => true);
		const regId = course.registrationId ? course.registrationId : getRegistrationIdFromCourseId(course.id, user.registrations);

		try {
			await mutateAsync({ id: regId });

			if (course.guestId) {
				await mutateAsync({ id: course.guestId });
			}

			await refetchUser();
			await queryClient.refetchQueries("myCourses");
			addNotification({
				className: "bg-green text-white",
				message: `Your registration for "${course.title}" has been cancelled.`,
				type: "success"
			});
			onCancel();
		} catch (error) {
			addNotification({
				message: error.message || "Error cancelling registration",
				type: "danger"
			});
		}
		setIsLoading(() => false);
	}, [courseToCancel]);

	return (
		<Modal centered show={show} size="lg">
			<Modal.Header>
				<Modal.Title>Cancel Course Registration</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className="mb-4">Are you sure you would like to cancel your registration for this course?</p>

				{courseToCancel ? <CoursesList courses={[courseToCancel]} selectable={false} showRemoveButton={false} /> : null}

				{courseToCancel?.series
					? (
						<Alert className="d-flex align-items-center" variant="warning">
							<ExclamationCircleIcon className="i fs-24 me-3" />
							<div className="flex-fill">
								<strong>This event is part of a series: {courseToCancel?.series}</strong>
								<br />
								{ /* eslint-disable-next-line max-len */ }
								If you would like to cancel your full series registration, please cancel individually for all other events in this series.
							</div>
						</Alert>
					) : null}

				{courseToCancel?.guestName
					? <Alert variant="warning">Guest Registration will also be cancelled: {courseToCancel?.guestName}</Alert> : null}
			</Modal.Body>
			<Modal.Footer>
				<Button className="me-2" disabled={isLoading} onClick={onCancel} variant="link">Cancel</Button>
				<Button disabled={isLoading} onClick={handleCancelReg} variant="danger">
					{isLoading ? <Spinner animation="border" size="sm" /> : "Confirm Cancellation"}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

CancelCourseRegistrationModal.propTypes = {
	course: PropType.shape({
		guestId: PropType.oneOfType([PropType.string, PropType.number]),
		id: PropType.oneOfType([PropType.string, PropType.number]),
		title: PropType.string,
		registrationId: PropType.oneOfType([PropType.string, PropType.number]),
		series: PropType.string
	}),
	onCancel: PropType.func.isRequired,
	show: PropType.bool
};

CancelCourseRegistrationModal.defaultProps = {
	course: null,
	show: false
};

export default CancelCourseRegistrationModal;
